import { Controller } from "@hotwired/stimulus";

/**
 * Reloads a form with its current values when a master field changes (e.g.
 * the class of a slot: its courses, periods and rooms change).
 *
 * <form data-controller="form-refresh" data-form-refresh-url-value="/…/new">
 *   <div data-action="change->form-refresh#refresh">…the class select…</div>
 *
 * Visits the URL by GET with the form's fields (without the token nor
 * _method), replacing the history entry. Given a `frame` (29/10/2026,
 * one-page forms), only that Turbo Frame is reloaded: the page keeps its
 * scroll and what was typed elsewhere. Given `fields` (name prefixes), only
 * the fields whose name starts with one of them are sent: a frame that only
 * needs the recipients does not carry a long message in its URL.
 *
 * An action may reload another frame from another URL with its params
 * (data-form-refresh-frame-param, data-form-refresh-url-param): the fields of
 * a choice redrawn in place (the reason of a radiation). All the fields are
 * then sent.
 */
export default class extends Controller {
  static values = { url: String, frame: String, fields: Array };

  refresh(event) {
    const params = (event && event.params) || {};
    const own = !params.frame;
    const base = params.url || this.urlValue;
    const data = new FormData(this.element);
    const query = new URLSearchParams();
    for (const [key, value] of data.entries()) {
      if (key === "authenticity_token" || key === "_method") continue;
      if (own && !this.wanted(key)) continue;
      if (typeof value === "string") query.append(key, value);
    }
    const url = `${base}${base.includes("?") ? "&" : "?"}${query}`;
    const frameId = params.frame || (this.hasFrameValue && this.frameValue);
    const frame = frameId && document.getElementById(frameId);
    if (frame) {
      frame.src = url;
    } else if (window.Turbo) {
      window.Turbo.visit(url, { action: "replace" });
    } else {
      window.location.replace(url);
    }
  }

  wanted(key) {
    if (!this.hasFieldsValue || this.fieldsValue.length === 0) return true;
    return this.fieldsValue.some((prefix) => key.startsWith(prefix));
  }
}
